import { FC, useCallback, useEffect, useState, useId } from "react";

import { SelectChangeEvent } from "@mui/material/Select";
import { FormType } from "@simplyk/common";

import { generateMenuItemOccurrences } from "../../../../../../../common/helpers/ticketingOccurrence";
import { useFormV2Context } from "../../../../contexts/FormV2Context";

import { useOccurrenceSelector } from "./OccurrenceSelectorContext";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { GHOST_PREVIEW_OPACITY } from "@/features/LiveFormEditor/LivePreview/ghostPreview.constant";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useIsGhostPreview } from "@/features/LiveFormEditor/LivePreview/useIsGhostPreview";
import {
  FormDateSelectField,
  FormDateSelectItem,
  FormDateSelectItemLabel,
} from "@/forms-design-system/FormDateSelectField";

export const TicketingV2OccurrenceSelector: FC = () => {
  const {
    selectedOccurrenceId,
    setSelectedOccurrenceId,
    occurrencesWithRates,
    formObject: ticketing,
  } = useFormV2Context(FormType.Ticketing);
  const { isOccurrenceSelectorForcedOpen, setOccurrenceSelectorForcedOpen } = useOccurrenceSelector();
  const { resetForm, form } = useFormV2Context(FormType.Ticketing);

  const { isPreview } = usePreviewContext();
  const isGhostPreview = useIsGhostPreview("TicketingV2OccurrenceSelector");

  const id = useId();

  const menuItemOccurrences = generateMenuItemOccurrences(occurrencesWithRates);
  const totalSelectableOccurrences = menuItemOccurrences.filter((occ) => !occ.isDisabled && !occ.isClosed).length;

  const [isOpen, setIsOpen] = useState(false);

  const handleOnChange = useCallback(
    (event: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
      const occurrenceId = event.target.value as string;
      setSelectedOccurrenceId(occurrenceId);
      setOccurrenceSelectorForcedOpen(false);
      resetForm();
    },
    [setOccurrenceSelectorForcedOpen, resetForm, setSelectedOccurrenceId]
  );

  // Open the select if the context signals it
  useEffect(() => {
    if (isOccurrenceSelectorForcedOpen) {
      setIsOpen(true);
      setOccurrenceSelectorForcedOpen(false);
    }
  }, [isOccurrenceSelectorForcedOpen, setOccurrenceSelectorForcedOpen]);

  const getDate = (date: number | Date | string | null | undefined) => {
    return date ? new Date(date) : new Date(); // The date should never be null / undefined, but fixing the type would be costly, so we leave it as-is for now
  };

  const handleRenderValue = useCallback(
    (occurrenceId: string) => {
      const item = menuItemOccurrences.find((occ) => occ.occurrenceId === occurrenceId);

      if (!item) {
        return null;
      }

      return (
        <FormDateSelectItemLabel
          startDate={getDate(item.startUtc)}
          endDate={getDate(item.endUtc)}
          isSoldOut={Boolean(item.isDisabled)}
          isClosed={Boolean(item.isClosed)}
          timezone={ticketing?.eventTimezone ?? null}
        />
      );
    },
    [menuItemOccurrences, ticketing?.eventTimezone]
  );

  if (occurrencesWithRates.length <= 1) {
    return <input type="hidden" {...form.register("occurrenceId")} value={selectedOccurrenceId || ""} />;
  }

  return (
    <FormDateSelectField
      id={id}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      value={selectedOccurrenceId ?? ""}
      disableInjectingGlobalStyles={isPreview} // We need this to make the LivePreview smooth
      count={totalSelectableOccurrences}
      sx={{
        opacity: isGhostPreview ? GHOST_PREVIEW_OPACITY : 1,
      }}
      renderValue={handleRenderValue}
      components={{
        select: {
          "data-test": "ticketing-occurrence-selector-select",
        },
      }}
      onChange={handleOnChange}
    >
      {menuItemOccurrences.map((occurrence) => {
        return (
          <FormDateSelectItem
            key={occurrence.occurrenceId}
            value={occurrence.occurrenceId}
            startDate={getDate(occurrence.startUtc)}
            endDate={getDate(occurrence.endUtc)}
            isSoldOut={Boolean(occurrence.isDisabled)}
            isClosed={Boolean(occurrence.isClosed)}
            wrapOnMobile
            timezone={ticketing?.eventTimezone ?? null}
          />
        );
      })}
    </FormDateSelectField>
  );
};
